import api from './api';

export interface User {
  id: number;
  user_id: string;
  full_name: string;
  email: string;
  email_verified_at?: string;
  phone_no?: string;
  role: {
    id: number;
    name: 'super-admin' | 'admin' | 'lawyer' | 'client' | 'staff';
    description: string;
    created_at: string;
    updated_at: string;
  };
  position: {
    id: number;
    name: string;
    created_at: string;
    updated_at: string;
  };
  web_token?: string;
  token?: string;
  status: string;
  created_at: string;
  updated_at: string;
  avatar?: string;
  two_factor_enabled?: boolean;
  firm_id?: number;
  firm?: any;
  organization_id?: number;
  avatar_url?: string;
  is_active?: boolean;
  last_login?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
  remember_me?: boolean;
}

export interface RegisterData {
  email: string;
  password: string;
  confirm_password: string;
  first_name: string;
  last_name: string;
  role?: 'lawyer' | 'client';
  organization_code?: string;
}

export interface AuthResponse {
  status: boolean;
  message: string;
  data: {
    user: User;
    access_token: string;
    refresh_token: string;
    expires_in?: number;
    token_type: string;
  };
}

export interface ResetPasswordData {
  email: string;
}

export interface ConfirmResetPasswordData {
  token: string;
  password: string;
  confirm_password: string;
}

export interface ChangePasswordData {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

class AuthService {
  private static instance: AuthService;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private tokenExpiry: Date | null = null;

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  constructor() {
    this.loadTokensFromStorage();
  }

  get isAuthenticated(): boolean {
    return !!this.accessToken
  }

  private loadTokensFromStorage(): void {
    this.accessToken = localStorage.getItem('access_token');
    this.refreshToken = localStorage.getItem('refresh_token');
    const expiry = localStorage.getItem('token_expiry');
    this.tokenExpiry = expiry ? new Date(expiry) : null;
  }

  public saveTokensToStorage(accessToken: string, refreshToken: string): void {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this.tokenExpiry = new Date(Date.now() + (3600 * 1000)); // 1 hour

    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('token_expiry', this.tokenExpiry.toISOString());
  }

  private clearTokensFromStorage(): void {
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiry = null;

    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expiry');
  }

  public getAccessToken(): string | null {
    if (this.accessToken && this.tokenExpiry && new Date() < this.tokenExpiry) {
      return this.accessToken;
    }
    return null;
  }

  public async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await api.post('/auth/login', credentials);
      const authData = response.data;
      
      // Store user data in localStorage for easy access
      localStorage.setItem('user_data', JSON.stringify(authData.data.user));
      
      // Save tokens with expires_in if provided
      this.accessToken = authData.data.access_token;
      this.refreshToken = authData.data.refresh_token;
      this.tokenExpiry = authData.data.expires_in ? 
        new Date(Date.now() + authData.data.expires_in * 1000) : null;

      localStorage.setItem('access_token', authData.data.access_token);
      localStorage.setItem('refresh_token', authData.data.refresh_token);
      if (authData.data.expires_in && this.tokenExpiry) {
        localStorage.setItem('token_expiry', this.tokenExpiry.toISOString());
      }

      // Set default authorization header for future requests
      api.defaults.headers.common['Authorization'] = `Bearer ${authData.data.access_token}`;
      
      return authData;
    } catch (error) {
      this.clearTokensFromStorage();
      throw error;
    }
  }

  public async register(data: RegisterData): Promise<AuthResponse> {
    try {
      const response = await api.post('/auth/register', data);
      const authData = response.data;
      
      // Store user data in localStorage for easy access
      localStorage.setItem('user_data', JSON.stringify(authData.data.user));
      
      // Save tokens with expires_in if provided
      this.accessToken = authData.data.access_token;
      this.refreshToken = authData.data.refresh_token;
      this.tokenExpiry = authData.data.expires_in ? 
        new Date(Date.now() + authData.data.expires_in * 1000) : null;

      localStorage.setItem('access_token', authData.data.access_token);
      localStorage.setItem('refresh_token', authData.data.refresh_token);
      if (authData.data.expires_in && this.tokenExpiry) {
        localStorage.setItem('token_expiry', this.tokenExpiry.toISOString());
      }

      api.defaults.headers.common['Authorization'] = `Bearer ${authData.data.access_token}`;
      
      return authData;
    } catch (error) {
      this.clearTokensFromStorage();
      throw error;
    }
  }

  public async logout(): Promise<void> {
    try {
      if (this.accessToken) {
        await api.post('/auth/logout', { refresh_token: this.refreshToken });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearTokensFromStorage();
      delete api.defaults.headers.common['Authorization'];
    }
  }

  public async refreshAccessToken(): Promise<string> {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await api.post('/auth/refresh', {
        refresh_token: this.refreshToken
      });
      
      const authData = response.data;
      
      // Save tokens with expires_in if provided
      this.accessToken = authData.data?.access_token || authData.access_token;
      this.refreshToken = authData.data?.refresh_token || authData.refresh_token;
      this.tokenExpiry = authData.data?.expires_in ? 
        new Date(Date.now() + authData.data.expires_in * 1000) : null;

      if (this.accessToken) {
        localStorage.setItem('access_token', this.accessToken);
      }
      if (this.refreshToken) {
        localStorage.setItem('refresh_token', this.refreshToken);
      }
      if (this.tokenExpiry) {
        localStorage.setItem('token_expiry', this.tokenExpiry.toISOString());
      }

      if (this.accessToken) {
        api.defaults.headers.common['Authorization'] = `Bearer ${this.accessToken}`;
      }
      
      return this.accessToken || '';
    } catch (error) {
      this.clearTokensFromStorage();
      delete api.defaults.headers.common['Authorization'];
      throw error;
    }
  }

  public async forgotPassword(data: ResetPasswordData): Promise<void> {
    await api.post('/auth/forgot-password', data);
  }

  public async resetPassword(data: ConfirmResetPasswordData): Promise<void> {
    await api.post('/auth/reset-password', data);
  }

  public async changePassword(data: ChangePasswordData): Promise<void> {
    await api.post('/auth/change-password', data);
  }

  public async getCurrentUser(): Promise<User> {
    const response = await api.get('/auth/me');
    return response.data;
  }

  public async updateProfile(data: Partial<User>): Promise<User> {
    const response = await api.put('/auth/profile', data);
    return response.data;
  }

  public async uploadAvatar(file: File): Promise<{ avatar_url: string }> {
    const formData = new FormData();
    formData.append('avatar', file);
    
    const response = await api.post('/auth/upload-avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  }

  // Setup axios interceptor for automatic token refresh
  public setupAxiosInterceptor(): void {
    api.interceptors.request.use(
      (config) => {
        const token = this.getAccessToken();
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const isAuthEndpoint = originalRequest?.url?.includes('/auth/login') ||
          originalRequest?.url?.includes('/auth/register');

        if (error.response?.status === 401 && !isAuthEndpoint && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const newToken = await this.refreshAccessToken();
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return api(originalRequest);
          } catch (refreshError) {
            this.clearTokensFromStorage();
            delete api.defaults.headers.common['Authorization'];
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }
}

export default AuthService.getInstance();
